import { Box, Spinner, Text, VStack } from '@chakra-ui/react';
import type React from 'react';

interface PageLoadingSpinnerProps {
  /**
   * Optional loading message displayed below the spinner
   */
  message?: string;
  /**
   * Size of the spinner
   * @default 'lg'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * PageLoadingSpinner - Full-page loading spinner component
 *
 * Displays a centered spinner below the header (not covering it).
 * Used for initial page loads and data fetching states.
 *
 * @example
 * ```tsx
 * if (isLoading) {
 *   return <PageLoadingSpinner message="Loading projects..." />;
 * }
 * ```
 */
export const PageLoadingSpinner: React.FC<PageLoadingSpinnerProps> = ({ message, size = 'lg' }) => {
  return (
    <Box
      w="100%"
      h="calc(100vh - 73px)" // Match content area height (accounts for header)
      display="flex"
      justifyContent="center"
      alignItems="center"
      bg="neutral.background"
    >
      <VStack gap={4}>
        <Spinner size={size} />
        {message && (
          <Text fontSize="sm" color="gray.600">
            {message}
          </Text>
        )}
      </VStack>
    </Box>
  );
};
